import { useFinanceApi } from 'infrastructure/api';
import vendorDocValidationSchema from './createupdate/vendorDocValidationSchema';
import { LoadingParams } from 'framework/list';
import { cloneDeep } from 'lodash';
import { useVendorId } from 'domain/finance/vendors/routes';
import { DocumentStorageContextData } from 'framework/documentStorage';
import { FormFieldsData } from 'framework/form';
import { useDocumentStorageReceiver, normalizeStoragePath } from 'domain/shared/documentStorage';
import { TokenDocumentTypes } from 'framework/documentStorage';

export const READ_VENDOR_DOCUMENTS = 'FINANCE_VENDOR_DOCUMENT_SEARCH';
export const UPLOAD_VENDOR_DOCUMENTS = 'FINANCE_VENDOR_DOCUMENT_UPLOAD';
export const DOWNLOAD_VENDOR_DOCUMENTS = 'FINANCE_VENDOR_DOCUMENT_DOWNLOAD';

const extractVendorIdFromPath = (path: string) => {
  const match = path?.match(/(?:^|\/)vendor\/([^/]+)/);
  return match?.[1];
};

const getVendorId = (routeVendorId: string, paths: string[] = [], path?: string) => {
  if (routeVendorId) {
    return routeVendorId;
  }

  if (path) {
    const extracted = extractVendorIdFromPath(path);
    if (extracted) {
      return extracted;
    }
  }

  for (const item of paths) {
    const extracted = extractVendorIdFromPath(item);
    if (extracted) {
      return extracted;
    }
  }

  return undefined;
};

const useVendorDocumentsService = (): DocumentStorageContextData => {
  const id = useVendorId();
  const { sendPostRequest } = useFinanceApi();

  const searchDocuments = async (requestParams: LoadingParams) => {
    try {
      const params = cloneDeep(requestParams);
      const searchString = params.filter?.['specification:search']?.eq || '';

      if (searchString && params.filter) {
        delete params.filter?.['specification:search'];
        params.filter.fileName = {
          ct: searchString,
        };
      }

      const request = await sendPostRequest('/vendors/documents/search', {
        ...params,
        filter: {
          ...params.filter,
          entityId: {
            eq: id,
          },
          entityName: {
            eq: 'vendor',
          },
        },
      });

      return request.json();
    } catch {
      return {
        result: [],
      };
    }
  };

  const getDocumentByPaths = useDocumentStorageReceiver(async (paths: string[]) => {
    const vendorId = getVendorId(id, paths);
    const request = await sendPostRequest('/vendors/documents/files', {
      paths,
      vendorId,
    });
    const response = await request.json();
    return response.result || [];
  });

  const uploadDocument = async (data: FormFieldsData) => {
    const request = await sendPostRequest('/vendors/documents/commit', {
      ...data,
    });
    return request.json();
  };

  const editDocument = async (data: FormFieldsData) => {
    await sendPostRequest('/vendors/documents/commit', {
      ...data,
    });
    return data; // To update in list
  };

  const getDocumentToken = async (path: string) => {
    const vendorId = getVendorId(id, [], path);
    const request = await sendPostRequest('/vendors/documents/token', {
      path: normalizeStoragePath(path),
      vendorId,
    });
    const response = (await request.json()) as TokenDocumentTypes;
    return response.metadata.preSignedUrl;
  };

  const getValidationSchema = () => Promise.resolve(vendorDocValidationSchema);

  return {
    searchDocuments,
    uploadDocument,
    editDocument,
    getDocumentToken,
    getValidationSchema,
    getDocumentByPaths,
  };
};

export default useVendorDocumentsService;
